(ns common.db.effects.http
  (:require [clojure.string :as str]
            [common.db.effects.util :as effects.util]
            [re-frame.core :as rf]))

(defn fetch!
  [uri m]
  (js/fetch uri (clj->js m)))

(defn append-query
  [uri m]
  (let [qs (str/join
            "&"
            (for [[k v] m]
              (str (name k) "=" (.encodeURIComponent js/window v))))]
    (if (seq qs)
      (str uri "?" qs)
      uri)))

(defn jsonify [m]
  (-> m clj->js js/JSON.stringify))

(defmulti ^:private format-uri (fn [v & _] v))
(defmethod format-uri :get [_ uri params] (append-query uri params))
(defmethod format-uri :delete [_ uri params] (append-query uri params))
(defmethod format-uri :default [_ uri _] uri)

(defmulti ^:private format-body-json (fn [v & _] v))
(defmethod format-body-json :default [_ _] nil)
(defmethod format-body-json :post [_ params] (jsonify params))
(defmethod format-body-json :patch [_ params] (jsonify params))
(defmethod format-body-json :put [_ params] (jsonify params))

(defn- generate-form-data
  [params]
  (let [form-data (js/FormData.)]
    (doseq [[k v] params]
      (.append form-data (name k) v))
    form-data))

(defmulti ^:private make-request! :format :default :json)

(defmethod make-request! :json
  [{:keys [uri method signal headers params]}]
  (let [uri (format-uri method uri params)
        body (format-body-json method params)
        headers (merge {"Content-Type" "application/json"} headers)]
    (fetch! uri {:headers headers :method (str/upper-case (name method)) :body body :signal signal})))

(defmethod make-request! :form-data
  [{:keys [uri method signal headers params]}]
  (let [body (generate-form-data params)]
    (fetch! uri {:headers headers :method (str/upper-case (name method)) :body body :signal signal})))

(defmethod make-request! :binary
  [{:keys [uri method headers signal params body]}]
  (let [uri (format-uri method uri params)
        headers (merge {"Content-Type" "application/octet-stream"} headers)]
    (fetch! uri {:headers headers :method (str/upper-case (name method)) :body body :signal signal})))

(defmethod make-request! :binary-with-offset
  [{:keys [uri method headers signal params body]}]
  (let [uri (format-uri method uri params)
        headers (merge {"Content-Type" "application/offset+octet-stream"} headers)]
    (fetch! uri {:headers headers :method (str/upper-case (name method)) :body body :signal signal})))

(defmethod make-request! :blank
  [{:keys [uri method headers signal params body]}]
  (let [uri (format-uri method uri params)]
    (fetch! uri {:headers headers :method (str/upper-case (name method)) :body body :signal signal})))

(defmulti parse-response*
  (fn [format _response] format))

(defmethod parse-response* :default
  [_ response]
  (parse-response* :text response))

(defmethod parse-response* :json
  [_ response]
  (.json response))

(defmethod parse-response* :text
  [_ response]
  (.text response))

(defmethod parse-response* :asset-create
  [_ response]
  (if (= (.-status response) 200)
    (-> (.json response)
        (.then (fn [body]
                 (js/Promise.resolve
                  {:status (.-status response)
                   :url (:url (js->clj body :keywordize-keys true))}))))
    (js/Promise.resolve {:status (.-status response)
                         :offset (.get (.-headers response) "Upload-Offset")
                         :location (.get (.-headers response) "Location")})))

(defmethod parse-response* :upload-part
  [_ response]
  (if (= (.-status response) 200)
    (-> (.json response)
        (.then (fn [body]
                 (js/Promise.resolve
                  {:status (.-status response)
                   :url (:url (js->clj body :keywordize-keys true))}))))
    (js/Promise.resolve {:status (.-status response)
                         :offset (.get (.-headers response) "Upload-Offset")})))

(defn- filename-from-content-disposition [content-disposition]
  (let [[_ filename] (re-matches #".*filename=(.+)" content-disposition)]
    filename))

;; reads body as a blob and expects to find name in the Content-Disposition header
(defmethod parse-response* :binary+filename
  [_ response]
  (-> (.blob response)
      (.then (fn [blob]
               (js/Promise.resolve
                {:filename (if-let [content-disposition (.get (.-headers response) "Content-Disposition")]
                             (filename-from-content-disposition content-disposition)
                             "download")
                 :blob blob})))))

(defn- parse-response!
  [response-format response]
  (js/Promise.
   (fn [resolve reject]
     (if (and (= (.-status response) 204)
              (not= response-format :upload-part))
       (resolve {:status 204
                 :ok? true
                 :value nil})
       (-> (parse-response* response-format response)
           (.then #(resolve {:status (.-status response)
                             :ok? (.-ok response)
                             :value (if (= :json response-format)
                                      (js->clj % :keywordize-keys true)
                                      %)}))
           (.catch reject))))))

(defn- http-request!
  [{:keys [response-format id] :as config
    :or {response-format :json}}]
  (js/Promise.
   (fn [resolve reject]
     (-> config
         (make-request!)
         (.then (partial parse-response! response-format))
         (.then #(if (:ok? %)
                   (resolve (if id
                              {id (:value %)}
                              (:value %)))
                   (reject %)))
         (.catch reject)))))

(defn- valid-http-request?
  "Check if a http request is valid.

   The validation is done by checking the existence/non-empty of the following
   keys: `#{:uri :method}`. To validate other keys, you can pass a second argument as
   a set of keys.

   Example:

   ```clojure
   (def config {:id :some-id
                :method :get
                :uri \"https://api.some-domain.io\"})

   (valid-http-request? config #{:id})
   ```"
  ([request] (valid-http-request? request #{}))
  ([request ks]
   {:pre [(set? ks)]}
   (every? #(some? (request %)) (conj ks :uri :method))))

(defmulti http-fx
  #(if (contains? % :requests)
     :multi
     :default))

;; Handling single request
;;
;; Example:
;;
;; {:http {:method :get
;;         :uri "https://api.example-domain/users"}
;;         :on-success [:on-success]
;;         :on-failure [:on-failure]}
(defmethod http-fx :default
  [{:keys [on-success on-failure] :as config}]
  {:pre [(valid-http-request? config)]}
  (-> (http-request! config)
      (.then #(effects.util/invoke-callback on-success %))
      (.catch #(effects.util/invoke-callback on-failure %))))

;; Handling multiple request parallel with `Promise.all`
;;
;; Example:
;;
;; {:http {:requests   [{:id :users-request
;;                       :method :get
;;                       :uri "https://api.example-domain/users"}
;;                      {:id :posts-request
;;                       :method :get
;;                       :uri "https://api.example-domain/posts"}]
;;         :on-success [:on-success]
;;         :on-failure [:on-failure]}}
(defmethod http-fx :multi
  [{:keys [requests on-success on-failure]}]
  {:pre [(every? valid-http-request? requests)]}
  (let [promises (map http-request! requests)]
    (-> (.all js/Promise promises)
        (.then #(effects.util/invoke-callback on-success (into {} %)))
        (.catch #(effects.util/invoke-callback on-failure %)))))

(rf/reg-fx :http http-fx)
